import React, { useCallback, useEffect, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";
import "react-loading-skeleton/dist/skeleton.css";

import { useLocationContext } from "../../contexts/locationContext";
import { formatTime } from "../../shared/utils/dateTime";
import { useUserContext } from "../../userDashboard/contexts/userContext";
import { handleGetPublicGames } from "../../userDashboard/userControllers/gameController";
import { toIso3 } from "../../utils/locationStorage";
import { isGameUpcoming } from "../../utils/matchLifecycle";
import { formatItemPrice } from "../../utils/price";

const encodeId = (id: string | number) => {
    return encodeURIComponent(btoa(id.toString()));
};

const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === "object" ? (value as Record<string, unknown>) : {};

interface Game {
    id: number;
    title: string;
    price?: number | string;
    currency?: string;
    currency_code?: string;
    country_code?: string;
    is_pro?: boolean | number;
    date: string;
    start_time: string;
    pitch?: {
        name?: string;
        location?: string;
    };
    venue?: string;
    location?: {
        city?: string;
        state?: string;
        country?: string;
    };
    city?: string;
    state?: string;
    country?: string;
    [key: string]: unknown;
}

const MatchLanding: React.FC = () => {
    const [games, setGames] = useState<Game[]>([]);
    const [loading, setLoading] = useState(true);
    const { location: storedLocation } = useLocationContext();
    const userContext = useUserContext();
    const userProfile = userContext?.userProfile ?? null;
    const storedCountryCode = storedLocation?.countryCode || "";
    const storedState = storedLocation?.state || "";
    const storedCity = storedLocation?.city || "";

    const effectiveCountryCode = useMemo(() => {
        const raw =
            userProfile?.country_code ||
            userProfile?.country ||
            storedCountryCode;
        return toIso3(raw as string) || "";
    }, [
        userProfile?.country_code,
        userProfile?.country,
        storedCountryCode,
    ]);

    const normalizeGames = (response: unknown): Game[] => {
        if (!response) return [];
        if (Array.isArray(response)) return response;
        const payload = asRecord(response);
        if (Array.isArray(payload.data)) return payload.data as Game[];
        const nested = asRecord(payload.data).data;
        if (Array.isArray(nested)) return nested as Game[];
        return [];
    };

    const getPriceLabel = (item: Game) =>
        formatItemPrice(
            {
                ...(item.price === undefined ? {} : { price: item.price }),
                ...(item.currency ? { currency: item.currency } : {}),
                ...(item.currency_code ? { currency_code: item.currency_code } : {}),
                ...((item.country_code || item.country) ? { country_code: item.country_code || item.country } : {}),
            },
            storedCountryCode
        );

    const getLocationLabel = (item: Game) => {
        if (!item) return "N/A";
        if (item.pitch?.name) return item.pitch.name;
        if (item.pitch?.location) return item.pitch.location;
        if (item.venue) return item.venue;

        const parts = [
            item.location?.city || item.city,
            item.location?.state || item.state,
            item.location?.country || item.country,
        ].filter(Boolean);

        if (parts.length) return parts.join(", ");
        return "N/A";
    };

    const fetchGames = useCallback(async () => {
        setLoading(true);
        try {
            const params: Record<string, string> = {};
            if (effectiveCountryCode) {
                params.country_code = effectiveCountryCode;
            }
            if (storedState) {
                params.state = storedState;
            }
            if (storedCity) {
                params.city = storedCity;
            }

            const data = await handleGetPublicGames(params);
            const list = normalizeGames(data).filter((game) => isGameUpcoming(game));
            setGames(list.slice(0, 6)); // Match the display grid logic
        } catch (error) {
            console.error("Failed to fetch games", error);
            setGames([]);
        } finally {
            setLoading(false);
        }
    }, [
        effectiveCountryCode,
        storedCity,
        storedState,
    ]);

    useEffect(() => {
        fetchGames();
    }, [fetchGames]);

    return (
        <div
            id="match-section"
            className="flex flex-col items-start justify-center py-12 md:py-16 w-full px-4 md:px-14"
        >
            <h3>Our Matches</h3>
            <h2 className="mt-3 font-Manrope">Check out our upcoming matches</h2>

            <div className="w-full mt-8">
                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3, 4, 5, 6].map((id) => (
                            <div key={`skeleton-${id}`} className="border border-gray-200 rounded-lg p-6 shadow-sm">
                                <div className="mb-4">
                                    <Skeleton width="80%" height={24} />
                                </div>
                                <div className="space-y-3">
                                    <Skeleton width="60%" height={16} />
                                    <Skeleton width="70%" height={16} />
                                    <Skeleton width="50%" height={16} />
                                    <Skeleton width="40%" height={16} />
                                </div>
                                <div className="mt-4">
                                    <Skeleton width="100%" height={40} />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {games.length === 0 ? (
                            <div className="col-span-1 sm:col-span-2 lg:col-span-3 text-center py-12">
                                <p className="font-Outfit text-[#667085] text-lg">
                                    There are no matches yet.
                                </p>
                            </div>
                        ) : (
                            games.map((item) => (
                                <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow duration-300">
                                    <div className="mb-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <h3 className="font-semibold text-lg text-[#101828] font-Manrope truncate">
                                                {item.title}
                                            </h3>
                                            {item.is_pro === true || item.is_pro === 1 ? (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-Manrope font-medium bg-[#FFF6E6] text-[#D4A017]">
                                                    Pro
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>

                                    <div className="space-y-3 mb-6">
                                        <div className="flex items-center text-sm text-[#667085]">
                                            <span className="font-medium text-[#344054] min-w-0 w-20">Location:</span>
                                            <span className="ml-2 truncate">{getLocationLabel(item)}</span>
                                        </div>
                                        <div className="flex items-center text-sm text-[#667085]">
                                            <span className="font-medium text-[#344054] min-w-0 w-20">Date:</span>
                                            <span className="ml-2">{item.date}</span>
                                        </div>
                                        <div className="flex items-center text-sm text-[#667085]">
                                            <span className="font-medium text-[#344054] min-w-0 w-20">Time:</span>
                                            <span className="ml-2">{formatTime(item.start_time)}</span>
                                        </div>
                                        <div className="flex items-center text-sm text-[#667085]">
                                            <span className="font-medium text-[#344054] min-w-0 w-20">Price:</span>
                                            <span className="ml-2 font-semibold text-[#317333]">{getPriceLabel(item)}</span>
                                        </div>
                                    </div>

                                    <Link
                                        to={`/detailed-match?id=${encodeId(
                                            item.id
                                        )}&title=${encodeURIComponent(item.title)}`}
                                        className="w-full inline-flex justify-center items-center py-3 px-4 bg-[#317333] hover:bg-[#2a5f2a] rounded-md text-white font-Manrope text-sm font-medium transition-colors duration-200"
                                    >
                                        View Match
                                    </Link>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>

            {games.length > 5 && (
                <div className="flex justify-center mt-8 md:mt-12 w-full">
                    <Link
                        to="/matches"
                        className="py-3 px-6 bg-[#317333] hover:bg-[#2a5f2a] rounded-[6px] font-Manrope text-[#fff] font-semibold text-[15px] transition-colors duration-200"
                    >
                        View more
                    </Link>
                </div>
            )}
        </div>
    );
};

export default MatchLanding;
